
import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Search } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts.index';
import BlogCard from '@/components/blog/BlogCard';
import { allSeries } from '@/data/series';
import SeriesCard from '@/components/series/SeriesCard';
import { isVisible } from '@/lib/draftPreview';

const BlogArchive = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPillar, setSelectedPillar] = useState('All');
  const [selectedCluster, setSelectedCluster] = useState('All');

  // Initialize filter from URL params
  useEffect(() => {
    const tagParam = searchParams.get('tag');
    if (tagParam) {
      setSearchQuery(tagParam);
    }
  }, [searchParams]);

  // Filter out draft posts - only show published posts
  const publishedPosts = useMemo(() => {
    return blogPosts.filter(post => post.status === 'published');
  }, []);

  // Series hubs (#546). Same gate as SeriesPage: on production hosts a series is
  // linked only if at least one member is published; off production drafts count.
  const visibleSeries = useMemo(
    () => allSeries.filter((series) => series.parts.some((p) => isVisible(blogPosts.find((b) => b.id === p.id)))),
    []
  );

  // Extract unique categories, pillars, and semantic clusters from published posts only
  const categories = ['All', ...new Set(publishedPosts.map(post => post.category))];
  const pillars = ['All', ...new Set(publishedPosts.map(post => post.pillar).filter(Boolean))];
  const clusters = ['All', ...new Set(publishedPosts.map(post => post.semanticCluster).filter(Boolean))];

  // Filter posts by search and category, then sort by date (newest first)
  const filteredPosts = useMemo(() => {
    const filtered = publishedPosts.filter(post => {
      const query = searchQuery.toLowerCase();
      const tags = Array.isArray(post.tags) ? post.tags : [];
      const matchesSearch = post.title.toLowerCase().includes(query) ||
                          post.excerpt.toLowerCase().includes(query) ||
                          tags.some(tag => tag.toLowerCase().includes(query));

      const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
      const matchesPillar = selectedPillar === 'All' || post.pillar === selectedPillar;
      const matchesCluster = selectedCluster === 'All' || post.semanticCluster === selectedCluster;

      return matchesSearch && matchesCategory && matchesPillar && matchesCluster;
    });

    // Sort by date (newest first)
    return filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateB - dateA; // Descending order (newest first)
    });
  }, [publishedPosts, searchQuery, selectedCategory, selectedPillar, selectedCluster]);

  return (
    <>
      <Helmet>
        <title>Blog - Educ4te Cybersecurity Insights</title>
        <meta name="description" content="Latest news, guides, and insights on Microsoft security, Active Directory, Entra ID, and cybersecurity training." />
        <link rel="canonical" href="https://educ4te.com/blog" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://educ4te.com/"},
            {"@type": "ListItem", "position": 2, "name": "Blog", "item": "https://educ4te.com/blog"}
          ]
        })}</script>
      </Helmet>

      <div className="pt-24 pb-20 bg-gray-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Search and Filter Bar - Compact and Sticky */}
          <div className="mb-6 md:mb-12 flex flex-col md:flex-row gap-3 md:gap-6 justify-between items-center bg-white p-3 md:p-6 rounded-xl md:rounded-2xl shadow-sm border border-gray-100 sticky top-20 md:static z-10 md:z-0">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search articles or tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm md:text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#422680] focus:border-transparent transition-all bg-gray-50 md:bg-white"
              />
            </div>
            
            <div className="flex gap-2 overflow-x-auto pb-1 md:pb-0 w-full md:w-auto no-scrollbar">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium whitespace-nowrap transition-all ${
                    selectedCategory === category
                      ? 'bg-[#422680] text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Pillar Filter - HIDDEN */}
          {/* <div className="mb-4 bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">Content Pillar</div>
            <div className="flex gap-2 flex-wrap">
              {pillars.map((pillar) => (
                <button
                  key={pillar}
                  onClick={() => setSelectedPillar(pillar)}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all ${
                    selectedPillar === pillar
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {pillar === 'Technology' && '⚙️'}
                  {pillar === 'People' && '👥'}
                  {pillar === 'Process' && '📋'}
                  {pillar !== 'All' && ' '}
                  {pillar}
                </button>
              ))}
            </div>
          </div> */}

          {/* Semantic Cluster Filter - HIDDEN */}
          {/* <div className="mb-6 md:mb-12 bg-white p-4 md:p-5 rounded-xl shadow-sm border border-gray-100">
            <div className="text-xs font-bold text-gray-500 uppercase mb-3 tracking-wide">Topic Cluster</div>
            <div className="flex gap-2 flex-wrap">
              {clusters.map((cluster) => (
                <button
                  key={cluster}
                  onClick={() => setSelectedCluster(cluster)}
                  className={`px-3 py-1.5 md:px-4 md:py-2 rounded-full text-xs md:text-sm font-medium transition-all ${
                    selectedCluster === cluster
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {cluster}
                </button>
              ))}
            </div>
          </div> */}

          {/* Header Text */}
          <div className="text-center mb-6 md:mb-16">
            <h1 className="text-2xl md:text-6xl font-bold mb-2 md:mb-6">
              <span className="bg-gradient-to-r from-[#422680] via-[#660F56] to-[#F54952] bg-clip-text text-transparent">
                Cybersecurity Insights
              </span>
            </h1>
            <p className="text-sm md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
              Expert analysis, tutorials, and industry updates.
            </p>
          </div>

          {/* Series hubs (#546) */}
          {visibleSeries.length > 0 && (
            <section aria-labelledby="blog-series-heading" className="mb-10 md:mb-12">
              <h2 id="blog-series-heading" className="text-lg md:text-2xl font-bold text-gray-900 mb-3">Article series</h2>
              <ul className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {visibleSeries.map((series) => (
                  <li key={series.slug}><SeriesCard series={series} /></li>
                ))}
              </ul>
            </section>
          )}

          {/* Posts Grid */}
          {filteredPosts.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-start">
              {filteredPosts.map((post, index) => (
                <BlogCard key={post.id} post={post} variant="archive" index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">No posts found</h3>
              <p className="text-gray-600">Try adjusting your search or filters.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogArchive;
